import {useSignupPageQuery} from './__generated__/SignupPage.graphql';
import useEmailSignup, {FormInputs} from './useEmailSignup';
import {ButtonColor} from '../../lib/components/design_system/Button/options';
import CheckboxGroup from '../../lib/components/design_system/form/CheckboxGroup';
import TextInput from '../../lib/components/design_system/form/TextInput';
import useAppTheme from '../../lib/components/design_system/hooks/useAppTheme';
import useMediaQueryGetter, {
  MediaQuery,
} from '../../lib/components/design_system/hooks/useMediaQueryGetter';
import TextLink from '../../lib/components/design_system/Link/TextLink';
import {ContentColor} from '../../lib/components/design_system/theme/useContentPalette';
import BodyText from '../../lib/components/design_system/typography/BodyText';
import Typography, {
  Size,
} from '../../lib/components/design_system/typography/Typography';
import FormSubmitButton from '../../lib/components/Form/FormSubmitButton';
import DefaultPageLayout from '../../lib/components/layout/DefaultPageLayout';
import Flex from '../../lib/components/layout/flex/Flex';
import FormPageContentWrapper from '../../lib/components/layout/FormPageContentWrapper';
import useForm, {FormState} from '../../lib/hooks/useForm';
import useNavigateToNextUrl, {
  NEXT_URL_SEARCH_PARAM_KEY,
} from '../../lib/hooks/useNavigateToNextUrl';
import useRecordFieldValidator from '../../lib/hooks/useRecordFieldValidator';
import useFormValidators from '../../lib/hooks/useRecordValidator';
import {linkToApp} from '../../lib/url/linkToApp';
import {linkToMarketing} from '../../lib/url/linkToMarketing';
import allowNonEmptyString from '../../lib/validators/allowNonEmptyString';
import alwaysDeny from '../../lib/validators/alwaysDeny';
import NotFound from '../NotFound';

import {css} from '@emotion/react';
import React, {useCallback, useEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

const COMMUNITY_GUIDELINES_CONSENT_KEY = 'community-guidelines';
const TERMS_OF_USE_CONSENT_KEY = 'terms-of-use';

export default function SignupPage(): JSX.Element {
  const {t} = useTranslation();
  const {data, loading: isLoading} = useSignupPageQuery();
  const {palettes, spacing} = useAppTheme();
  const [consents, setConsents] = useState<string[]>([]);
  const allConsentGiven =
    consents.includes(COMMUNITY_GUIDELINES_CONSENT_KEY) &&
    consents.includes(TERMS_OF_USE_CONSENT_KEY);

  const navigateToNext = useNavigateToNextUrl(linkToApp('/'));
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (!isLoading && data?.viewer?.id != null) {
      navigateToNext({replace: true});
    }
  }, [data?.viewer?.id, isLoading, navigateToNext]);

  const commit = useEmailSignup(searchParams.get(NEXT_URL_SEARCH_PARAM_KEY));
  const onSubmit = useCallback(
    async (inputs: FormInputs) => {
      await commit(inputs.email);
    },
    [commit],
  );
  const emailValidator = useRecordFieldValidator<FormInputs, 'email'>(
    'email',
    allowNonEmptyString(
      alwaysDeny(
        t('validation.missing-required-field', {
          name: t('email-credential.email-address'),
        }),
      ),
    ),
  );
  const onValidate = useFormValidators([emailValidator]);
  const formObject = useForm<FormInputs>({
    fallbackFormErrors: [t('form.unknown-form-submission-error')],
    onSubmit,
    onValidate,
    options: {
      forbidSubmissionWhenSuccess: true,
    },
    startingValue: {
      email: '',
    },
  });
  const {doSubmit, fieldErrors, fieldValues, formState, setFieldValue} =
    formObject;
  const getMediaQuery = useMediaQueryGetter();
  const hasError =
    formState == FormState.VALIDATED_INVALID ||
    formState === FormState.SUBMITTED_ERROR;

  if (isLoading) {
    return <div>{t('common-strings.loading')}</div>;
  }

  if (!data?.featureFlags.signup) {
    return <NotFound />;
  }

  let content;

  if (formState === FormState.SUBMITTED_SUCCESS) {
    content = (
      <div
        css={css({
          marginTop: spacing.x32,
        })}
      >
        <Typography size={Size.L}>{t('pages.signup.signup-done')}</Typography>
      </div>
    );
  } else {
    content = (
      <>
        <Flex
          css={css({
            marginBottom: spacing.x20,
            marginTop: spacing.x20,
            textAlign: 'center',
            whiteSpace: 'pre',
          })}
        >
          <BodyText size="S">
            {t('pages.signup.account-holder-instruction')}
          </BodyText>
          <TextLink
            textProps={{
              color: ContentColor.SECONDARY,
            }}
            to={linkToApp('/login', searchParams)}
          >
            {t('pages.login.signup-here-link-label')}
          </TextLink>
        </Flex>
        <form
          css={css({width: 320})}
          onSubmit={(e) => void doSubmit(e)}
        >
          <TextInput
            errorMessage={fieldErrors.email}
            isInvalid={hasError && fieldErrors.email != null}
            isRequired={true}
            label={t('email-credential.email-address')}
            name="email"
            type="email"
            value={fieldValues.email}
            onChange={(value) => setFieldValue('email', value)}
          />

          <CheckboxGroup
            aria-label={t('pages.signup.terms-of-use-link-label')}
            options={[
              {
                ariaLabel: t('pages.signup.community-guideline-link-label'),
                id: 'communityGuideline',
                label: (
                  <BodyText
                    size="S"
                    sx={css({
                      display: 'flex',
                      whiteSpace: 'pre',
                    })}
                  >
                    <Trans
                      components={{
                        a: (
                          <TextLink
                            textProps={{color: ContentColor.SECONDARY}}
                            to={linkToMarketing('/community-rules')}
                          />
                        ),
                      }}
                      i18nKey="pages.signup.community-guidelines"
                    />
                  </BodyText>
                ),
                value: COMMUNITY_GUIDELINES_CONSENT_KEY,
              },
              {
                ariaLabel: t('pages.signup.terms-of-use-link-label'),
                id: 'termsOfUse',
                label: (
                  <BodyText
                    size="S"
                    sx={css({
                      display: 'flex',
                      whiteSpace: 'pre',
                    })}
                  >
                    <Trans
                      components={{
                        a1: (
                          <TextLink
                            textProps={{color: ContentColor.SECONDARY}}
                            to={linkToMarketing('/terms-of-use')}
                          />
                        ),
                        a2: (
                          <TextLink
                            textProps={{color: ContentColor.SECONDARY}}
                            to={linkToMarketing('/privacy-policy')}
                          />
                        ),
                      }}
                      i18nKey="pages.signup.terms-of-use-privacy-policy"
                    />
                  </BodyText>
                ),
                value: TERMS_OF_USE_CONSENT_KEY,
              },
            ]}
            rootStyle={css({
              marginTop: spacing.x20,
            })}
            value={consents}
            onChange={setConsents}
          />

          <FormSubmitButton
            color={ButtonColor.ACCENT}
            formObject={formObject}
            idleLabel={t('pages.signup.signup-button-label')}
            isDisabled={!allConsentGiven}
            progressLabel={t('form.submitting')}
            sx={css({
              marginTop: spacing.x40,
              width: '100%',
            })}
          />
        </form>
        <div />
      </>
    );
  }

  return (
    <DefaultPageLayout
      showViewerNavControls={false}
      sx={css({
        backgroundColor: palettes.background.neutral.disabled,
      })}
    >
      <FormPageContentWrapper
        sx={css({
          height: '85vh',
        })}
      >
        <div
          css={css({
            width: 360,
            [getMediaQuery(MediaQuery.ExtraSmall)]: {
              width: '100%',
            },
          })}
        >
          <Typography
            rootStyle={css({
              textAlign: 'center',
            })}
            size={Size.XL}
            variant="h1"
            weight={700}
          >
            {t('pages.signup.signup-form-title', {brand: t('common.brand')})}
          </Typography>
        </div>
        {content}
      </FormPageContentWrapper>
    </DefaultPageLayout>
  );
}
